import { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { brl } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { FileDown, Printer, RefreshCw, FileText } from "lucide-react";

const today = new Date();
const toInputDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const firstDay = toInputDate(new Date(today.getFullYear(), today.getMonth(), 1));
const todayStr = toInputDate(today);

const fmtDate = (value) => {
  if (!value) return "—";
  const [y, m, d] = value.slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : value;
};

const fmtDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString("pt-BR");
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

export default function PartsUsedReport() {
  const [machines, setMachines] = useState([]);
  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(todayStr);
  const [machineId, setMachineId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    api.get("/reports/machines")
      .then((r) => setMachines(r.data || []))
      .catch((e) => toast.error(apiErr(e)));
  }, []);

  const params = useMemo(() => ({
    start_date: startDate,
    end_date: endDate,
    machine_id: machineId || undefined,
  }), [startDate, endDate, machineId]);

  const load = async () => {
    if (!startDate || !endDate) {
      toast.error("Informe a data inicial e a data final.");
      return;
    }
    setLoading(true);
    try {
      const { data: result } = await api.get("/reports/parts-used", { params });
      setData(result);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const response = await api.get("/reports/parts-used/pdf", {
        params,
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pecas_usadas_${startDate}_a_${endDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setPdfBusy(false);
    }
  };

  const printReport = () => {
    if (!data) return;
    const w = window.open("", "_blank", "width=1100,height=800");
    if (!w) {
      toast.error("O navegador bloqueou a janela de impressão.");
      return;
    }

    const rows = (data.rows || []).map((r) => `
      <tr>
        <td>${escapeHtml(r.machine_name)}</td>
        <td>${escapeHtml(r.codigo_interno || "—")}</td>
        <td>${escapeHtml(r.part_desc)}</td>
        <td class="num">${escapeHtml(r.quantidade_utilizada)}</td>
        ${data.finance ? `<td class="num">${escapeHtml(brl(r.valor_unitario))}</td><td class="num">${escapeHtml(brl(r.valor_total))}</td>` : ""}
        <td class="num">${escapeHtml(r.confirmacoes)}</td>
        <td>${escapeHtml(fmtDateTime(r.ultimo_uso))}</td>
      </tr>
    `).join("");

    w.document.write(`
      <!doctype html>
      <html lang="pt-BR">
        <head>
          <meta charset="utf-8" />
          <title>Relatório de Peças Usadas por Máquina</title>
          <style>
            @page { size: A4 portrait; margin: 12mm; }
            body { font-family: Arial, sans-serif; color: #111827; font-size: 11px; }
            h1 { text-align: center; font-size: 20px; margin: 0 0 14px; }
            .meta { margin-bottom: 3px; }
            .summary { display: flex; gap: 12px; margin: 14px 0; }
            .box { border: 1px solid #cbd5e1; padding: 8px 12px; min-width: 130px; }
            .box strong { display: block; margin-bottom: 4px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #cbd5e1; padding: 6px; vertical-align: top; }
            th { background: #e2e8f0; text-align: left; }
            .num { text-align: right; }
            .footer { margin-top: 12px; color: #64748b; font-size: 9px; }
          </style>
        </head>
        <body>
          <h1>Relatório de Peças Usadas por Máquina</h1>
          <div class="meta"><strong>Empresa:</strong> ${escapeHtml(data.empresa)}</div>
          <div class="meta"><strong>Período:</strong> ${escapeHtml(fmtDate(data.periodo?.inicio))} a ${escapeHtml(fmtDate(data.periodo?.fim))}</div>
          <div class="meta"><strong>Máquina:</strong> ${escapeHtml(data.machine_name || "Todas as máquinas")}${data.machine_equipment_id ? " (" + escapeHtml(data.machine_equipment_id) + ")" : ""}</div>
          <div class="summary">
            <div class="box"><strong>Total utilizado</strong>${escapeHtml(data.total_utilizado)} un.</div>
            <div class="box"><strong>Peças distintas</strong>${escapeHtml(data.total_pecas_distintas)}</div>
            <div class="box"><strong>Máquinas</strong>${escapeHtml(data.total_maquinas)}</div>
            <div class="box"><strong>Confirmações</strong>${escapeHtml(data.total_registros)}</div>
            ${data.finance ? `<div class="box"><strong>Valor total</strong>${escapeHtml(brl(data.valor_total_geral))}</div>` : ""}
          </div>
          <table>
            <thead>
              <tr><th>Máquina</th><th>Código</th><th>Peça</th><th>Qtd. usada</th>${data.finance ? "<th>Valor unit.</th><th>Valor total</th>" : ""}<th>Confirmações</th><th>Último uso</th></tr>
            </thead>
            <tbody>
              ${rows || `<tr><td colspan="${data.finance ? 8 : 6}" style="text-align:center;padding:20px">Nenhuma peça utilizada no período.</td></tr>`}
              ${data.finance && rows ? `<tr><td colspan="5" style="text-align:right;font-weight:bold">TOTAL GERAL</td><td class="num" style="font-weight:bold">${escapeHtml(brl(data.valor_total_geral))}</td><td colspan="2"></td></tr>` : ""}
            </tbody>
          </table>
          <div class="footer">Gerado pelo Peça360 em ${escapeHtml(new Date().toLocaleString("pt-BR"))}</div>
          <script>window.onload = () => { window.focus(); window.print(); };</script>
        </body>
      </html>
    `);
    w.document.close();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-50 flex items-center gap-2">
          <FileText className="h-7 w-7 text-sky-400" /> Relatório de Peças Usadas
        </h1>
        <p className="text-slate-400 text-sm">
          Consumo confirmado pela manutenção, agrupado por máquina e peça.
        </p>
      </div>

      <Card className="card-metal p-5">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <div>
            <Label>Data inicial</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1 bg-slate-950 border-slate-700" />
          </div>
          <div>
            <Label>Data final</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1 bg-slate-950 border-slate-700" />
          </div>
          <div>
            <Label>Máquina</Label>
            <select
              value={machineId}
              onChange={(e) => setMachineId(e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 text-sm text-slate-100"
            >
              <option value="">Todas as máquinas</option>
              {machines.map((m) => (
                <option key={m.id} value={m.id}>{m.nome}{m.equipment_id ? ` · ${m.equipment_id}` : ""}</option>
              ))}
            </select>
          </div>
          <Button onClick={load} disabled={loading} className="bg-sky-700 hover:bg-sky-600">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Gerar relatório
          </Button>
        </div>
      </Card>

      {loading && !data ? (
        <Skeleton className="h-80 bg-slate-800" />
      ) : data ? (
        <>
          <div className={`grid grid-cols-2 ${data.finance ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-3`}>
            <Card className="card-metal p-4"><div className="text-xs uppercase text-slate-500">Total utilizado</div><div className="text-2xl font-bold text-slate-100 font-mono">{data.total_utilizado} un.</div></Card>
            <Card className="card-metal p-4"><div className="text-xs uppercase text-slate-500">Peças distintas</div><div className="text-2xl font-bold text-slate-100 font-mono">{data.total_pecas_distintas}</div></Card>
            <Card className="card-metal p-4"><div className="text-xs uppercase text-slate-500">Máquinas</div><div className="text-2xl font-bold text-slate-100 font-mono">{data.total_maquinas}</div></Card>
            <Card className="card-metal p-4"><div className="text-xs uppercase text-slate-500">Confirmações</div><div className="text-2xl font-bold text-slate-100 font-mono">{data.total_registros}</div></Card>
            {data.finance && <Card className="card-metal p-4"><div className="text-xs uppercase text-slate-500">Valor total</div><div className="text-2xl font-bold text-emerald-300 font-mono">{brl(data.valor_total_geral)}</div></Card>}
          </div>

          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="secondary" onClick={printReport}>
              <Printer className="h-4 w-4 mr-2" /> Imprimir
            </Button>
            <Button onClick={downloadPdf} disabled={pdfBusy} className="bg-emerald-700 hover:bg-emerald-600">
              <FileDown className="h-4 w-4 mr-2" /> {pdfBusy ? "Gerando PDF..." : "Exportar PDF"}
            </Button>
          </div>

          <Card className="card-metal p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="bg-slate-950 text-slate-400 text-xs uppercase font-mono">
                    <th className="text-left px-3 py-3">Máquina</th>
                    <th className="text-left px-3 py-3">Código</th>
                    <th className="text-left px-3 py-3">Peça</th>
                    <th className="text-right px-3 py-3">Qtd. usada</th>
                    {data.finance && <th className="text-right px-3 py-3">Valor unit.</th>}
                    {data.finance && <th className="text-right px-3 py-3">Valor total</th>}
                    <th className="text-right px-3 py-3">Confirmações</th>
                    <th className="text-left px-3 py-3">Último uso</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.rows || []).map((r) => (
                    <tr key={`${r.machine_id || "sem"}-${r.part_id || r.part_desc}`} className="border-t border-slate-800">
                      <td className="px-3 py-2.5 text-slate-200">{r.machine_name}</td>
                      <td className="px-3 py-2.5 font-mono text-sky-300">{r.codigo_interno || "—"}</td>
                      <td className="px-3 py-2.5 text-slate-100">{r.part_desc}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-emerald-300 font-bold">{r.quantidade_utilizada}</td>
                      {data.finance && <td className="px-3 py-2.5 text-right font-mono text-slate-300">{brl(r.valor_unitario)}</td>}
                      {data.finance && <td className="px-3 py-2.5 text-right font-mono text-emerald-300">{brl(r.valor_total)}</td>}
                      <td className="px-3 py-2.5 text-right font-mono text-slate-300">{r.confirmacoes}</td>
                      <td className="px-3 py-2.5 text-slate-400 whitespace-nowrap">{fmtDateTime(r.ultimo_uso)}</td>
                    </tr>
                  ))}
                  {(data.rows || []).length === 0 && (
                    <tr><td colSpan={data.finance ? 8 : 6} className="px-4 py-12 text-center text-slate-500">Nenhuma peça utilizada no período selecionado.</td></tr>
                  )}
                  {data.finance && (data.rows || []).length > 0 && (
                    <tr className="border-t-2 border-slate-700 bg-slate-950/70">
                      <td colSpan={5} className="px-3 py-3 text-right font-bold text-slate-200">TOTAL GERAL</td>
                      <td className="px-3 py-3 text-right font-mono font-bold text-emerald-300">{brl(data.valor_total_geral)}</td>
                      <td colSpan={2}></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      ) : null}
    </div>
  );
}
